import React, { useState, useEffect } from 'react';
import {
  Calendar,
  Users,
  CreditCard,
  Shield,
  BarChart3,
  Activity,
  Settings,
  CheckSquare,
  FileText,
  ChevronLeft,
  ChevronRight,
  MessageCircle,
  Heart,
  AlertTriangle,
  Briefcase,
  MessagesSquare
} from 'lucide-react';
// import RecentlyViewed from './RecentlyViewed';
import { useMessaging } from '../contexts/MessagingContext';
import { useActivity } from '../contexts/ActivityContext';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types/roles';
import { isClinicalStaff, canManageBilling, hasAnyRole } from '../utils/permissions';
import { RoleUser } from '../utils/permissions';
import FirebaseEmergencyCancellationService from '../services/firebase/FirebaseEmergencyCancellationService';
import { firebaseTeamMessagingService } from '../services/firebase/FirebaseTeamMessagingService';
import PracticeRolesService, { PermissionKey } from '../services/PracticeRolesService';

interface SidebarItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  active?: boolean;
  badge?: number;
  onClick: () => void;
  requiredRoles?: UserRole[]; // Legacy fallback — only used if no dynamic permissions loaded
  requiredPermission?: PermissionKey; // Dynamic permission key from PracticeRolesService
}

interface SidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  onClientClick?: (clientId: string) => void;
  practiceOwnerId?: string; // Resolved practice owner UID for loading practice_roles
}

const Sidebar: React.FC<SidebarProps> = ({
  activeTab,
  onTabChange,
  collapsed = false,
  onToggleCollapse,
  onClientClick,
  practiceOwnerId: propPracticeOwnerId
}) => {
  // Get unread message count from messaging context
  const { totalUnreadCount } = useMessaging();
  // Get unread activity count from activity context
  const { unreadCount: activityUnreadCount } = useActivity();
  // Get auth context for provider ID and roles
  const { user, roles, userProfile } = useAuth();
  // Emergency cancellation pending count
  const [emergencyCancellationCount, setEmergencyCancellationCount] = useState(0);
  // Team Chat unread count
  const [teamChatUnreadCount, setTeamChatUnreadCount] = useState(0);
  // Dynamic roles from PracticeRolesService
  const [dynamicRolesReady, setDynamicRolesReady] = useState(false);

  // Build RoleUser for permission checks
  const roleUser: RoleUser | null = user && roles.length > 0 ? {
    uid: user.uid,
    roles,
    primaryRole: roles[0],
    email: user.email || undefined,
  } : null;

  const practiceOwnerId = propPracticeOwnerId || user?.uid || '';
  const rolesService = PracticeRolesService.getInstance();

  // Initialize PracticeRolesService for dynamic permissions
  useEffect(() => {
    if (!practiceOwnerId) return;
    rolesService.initialize(practiceOwnerId);
    const handleUpdate = () => setDynamicRolesReady(true);
    rolesService.subscribe(handleUpdate);
    setDynamicRolesReady(true);
    return () => rolesService.unsubscribe(handleUpdate);
  }, [practiceOwnerId]);

  // Settings roles (always system_admin + clinical_director — not customizable)
  const SETTINGS_ROLES: UserRole[] = ['system_admin', 'clinical_director'];

  // Filter items based on dynamic permissions from PracticeRolesService
  const filterByRole = (items: SidebarItem[]): SidebarItem[] => {
    if (!roleUser || roles.length === 0) return items; // No roles = show all (backward compat)

    return items.filter(item => {
      // If item has a dynamic permission key, check PracticeRolesService
      if (item.requiredPermission && dynamicRolesReady) {
        // Check if ANY of the user's roles grant this permission
        return roles.some(userRole => rolesService.hasPermission(userRole, item.requiredPermission!));
      }

      // Fallback to legacy requiredRoles check
      if (!item.requiredRoles || item.requiredRoles.length === 0) return true;
      return item.requiredRoles.some(r => roles.includes(r));
    });
  };

  // Subscribe to emergency cancellation pending count
  useEffect(() => {
    if (!user?.uid) return;

    const cancellationService = FirebaseEmergencyCancellationService.getInstance();
    const unsubscribe = cancellationService.subscribeToPendingCount(
      user.uid,
      (count) => setEmergencyCancellationCount(count)
    );

    return () => unsubscribe();
  }, [user?.uid]);

  // Subscribe to team chat unread count
  useEffect(() => {
    if (!user?.uid) return;
    const unsubscribe = firebaseTeamMessagingService.subscribeToUnreadCount(
      user.uid,
      (count) => setTeamChatUnreadCount(count)
    );
    return () => unsubscribe();
  }, [user?.uid]);

  // Client/Patient Care Functions
  const sidebarItems: SidebarItem[] = [
    {
      id: 'overview',
      label: 'Overview',
      icon: <Calendar size={20} />,
      onClick: () => onTabChange('overview')
      // All roles see Overview — no permission needed
    },
    {
      id: 'schedule',
      label: 'Appointments & Schedule',
      icon: <Calendar size={20} />,
      onClick: () => onTabChange('schedule'),
      requiredPermission: 'scheduling',
    },
    {
      id: 'clients',
      label: 'Clients',
      icon: <Users size={20} />,
      onClick: () => onTabChange('clients'),
      requiredPermission: 'patient_data',
    },
    {
      id: 'inquiries',
      label: 'Inquiries',
      icon: <FileText size={20} />,
      onClick: () => onTabChange('inquiries'),
      requiredPermission: 'patient_data',
    },
    {
      id: 'sessions',
      label: 'Sessions',
      icon: <BarChart3 size={20} />,
      onClick: () => onTabChange('sessions'),
      requiredPermission: 'session_notes',
    },
    {
      id: 'prescriptions',
      label: 'Prescriptions',
      icon: <Shield size={20} />,
      onClick: () => onTabChange('prescriptions'),
      requiredPermission: 'prescriptions',
    },
    {
      id: 'messages',
      label: 'Messages',
      icon: <MessageCircle size={20} />,
      badge: totalUnreadCount > 0 ? totalUnreadCount : undefined,
      onClick: () => onTabChange('messages'),
      requiredPermission: 'messages',
    },
    {
      id: 'team-chat',
      label: 'Team Chat',
      icon: <MessagesSquare size={20} />,
      badge: teamChatUnreadCount > 0 ? teamChatUnreadCount : undefined,
      onClick: () => onTabChange('team-chat'),
      requiredPermission: 'team_chat',
    },
    {
      id: 'emergency-cancellations',
      label: 'Emergency Cancellations',
      icon: <AlertTriangle size={20} />,
      badge: emergencyCancellationCount > 0 ? emergencyCancellationCount : undefined,
      onClick: () => onTabChange('emergency-cancellations'),
      requiredPermission: 'scheduling',
    }
  ];

  // Practice Management (daily workflow items only)
  const bottomItems: SidebarItem[] = [
    {
      id: 'tasks',
      label: 'Tasks',
      icon: <CheckSquare size={20} />,
      onClick: () => onTabChange('tasks')
      // All roles
    },
    {
      id: 'client-activity',
      label: 'Client Activity',
      icon: <Heart size={20} />,
      badge: activityUnreadCount > 0 ? activityUnreadCount : undefined,
      onClick: () => onTabChange('client-activity'),
      requiredPermission: 'activity_log',
    },
    {
      id: 'billing',
      label: 'Billing',
      icon: <CreditCard size={20} />,
      onClick: () => onTabChange('billing'),
      requiredPermission: 'billing',
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: <BarChart3 size={20} />,
      onClick: () => onTabChange('analytics'),
      requiredPermission: 'analytics',
    },
    {
      id: 'meetings',
      label: 'Staff Meetings',
      icon: <Activity size={20} />,
      onClick: () => onTabChange('meetings'),
      requiredPermission: 'staff_meetings',
    },
    {
      id: 'hr',
      label: 'HR & Documents',
      icon: <Briefcase size={20} />,
      onClick: () => onTabChange('hr')
      // Always visible — own HR docs always accessible; hr_all controls seeing others' docs
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: <Settings size={20} />,
      onClick: () => onTabChange('settings'),
      requiredRoles: SETTINGS_ROLES // Settings always admin/director only
    }
  ];

  const SidebarItemComponent: React.FC<{ item: SidebarItem }> = ({ item }) => (
    <button
      onClick={item.onClick}
      aria-label={collapsed ? item.label : undefined}
      aria-current={activeTab === item.id ? 'page' : undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: collapsed ? '0' : '12px',
        padding: collapsed ? '12px 8px' : '12px 16px',
        margin: '2px 8px',
        borderRadius: '8px',
        cursor: 'pointer',
        backgroundColor: activeTab === item.id ? '#28527A' : 'transparent',
        color: activeTab === item.id ? '#F7E9A0' : '#e5e7eb',
        transition: 'all 0.2s ease',
        borderLeft: activeTab === item.id ? '3px solid #D4A017' : '3px solid transparent',
        justifyContent: collapsed ? 'center' : 'flex-start',
        width: 'calc(100% - 16px)',
        border: 'none',
        textAlign: 'left',
        fontFamily: 'inherit'
      }}
      onMouseEnter={(e) => {
        if (activeTab !== item.id) {
          e.currentTarget.style.backgroundColor = '#28527A';
          e.currentTarget.style.color = '#F7E9A0';
        }
      }}
      onMouseLeave={(e) => {
        if (activeTab !== item.id) {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.color = '#e5e7eb';
        }
      }}
    >
      <div style={{ minWidth: '20px', display: 'flex', justifyContent: 'center' }} aria-hidden="true">
        {item.icon}
      </div>
      {!collapsed && (
        <>
          <span style={{ fontSize: '14px', fontWeight: '500' }}>
            {item.label}
          </span>
          {item.badge && (
            <div
              aria-label={`${item.badge} unread`}
              style={{
                backgroundColor: '#D4A017',
                color: 'white',
                borderRadius: '12px',
                padding: '2px 8px',
                fontSize: '12px',
                fontWeight: '600',
                marginLeft: 'auto'
              }}
            >
              {item.badge}
            </div>
          )}
        </>
      )}
    </button>
  );

  return (
    <nav aria-label="Sidebar navigation" style={{
      width: collapsed ? '80px' : '280px',
      height: '100vh',
      backgroundColor: '#16325B',
      borderRight: '1px solid #6CA6C1',
      display: 'flex',
      flexDirection: 'column',
      transition: 'width 0.3s ease',
      position: 'fixed',
      left: 0,
      top: 0,
      zIndex: 1000
    }}>
      {/* Header */}
      <div style={{
        padding: collapsed ? '16px 8px' : '16px',
        borderBottom: '1px solid #6CA6C1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'space-between'
      }}>
        {!collapsed && (
          <h2 style={{
            fontSize: '18px',
            fontWeight: '700',
            color: '#F7E9A0',
            margin: 0
          }}>
            My ClinicCore
          </h2>
        )}
        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: '#e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {collapsed ? <ChevronRight size={16} aria-hidden="true" /> : <ChevronLeft size={16} aria-hidden="true" />}
          </button>
        )}
      </div>

      {/* Main Navigation */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        paddingTop: '16px',
        overflowY: 'auto'
      }}>
        {/* Client/Patient Care Section Header */}
        <div role="group" aria-labelledby="sidebar-section-care">
          {!collapsed && (
            <h3 id="sidebar-section-care" style={{
              padding: '8px 16px',
              fontSize: '11px',
              fontWeight: '600',
              color: '#D4A017',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '4px',
              margin: 0
            }}>
              Client / Patient Care
            </h3>
          )}
          {filterByRole(sidebarItems).map((item) => (
            <SidebarItemComponent key={item.id} item={item} />
          ))}
        </div>

        {/* Practice Management Section */}
        <div role="group" aria-labelledby="sidebar-section-management" style={{ paddingTop: '16px', borderTop: '1px solid #374151', marginTop: '8px' }}>
          {!collapsed && (
            <h3 id="sidebar-section-management" style={{
              padding: '8px 16px',
              fontSize: '11px',
              fontWeight: '600',
              color: '#D4A017',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '4px',
              margin: 0
            }}>
              Practice Management
            </h3>
          )}
          {filterByRole(bottomItems).map((item) => (
            <SidebarItemComponent key={item.id} item={item} />
          ))}
        </div>
      </div>
    </nav>
  );
};

export default Sidebar;